import { applyMove, legalMoves } from '../game/game';
import { pieceById } from '../game/movement';
import type { FactionId, GameContext, GameState, Move, Piece } from '../game/types';
import { fightWinners } from './fightOutcomes';
import { pieceWorth } from './threats';

/**
 * What sitting out a turn costs a side that joins a fight, in evaluation points (a piece is worth
 * 20 to 110): about a fifth of a knight. Joining has to be clearly worth more than letting the
 * others fight it out, which is already a gain to a third side.
 */
export const MUSTER_TURN_COST = 10;
/** A power point is worth this much to whoever holds it, as the evaluation counts it. */
const POWER_POINT_VALUE = 12;

/**
 * The board AI's answer to a muster call: join with the piece whose fight is worth most to its
 * side, or pass. Each answer is judged by what the fight would change on the board, weighed by its
 * odds (the public ones, as the search reads them), with any later responders taken to pass; a
 * join also costs its side the turn it sits out (`MUSTER_TURN_COST`). Every AI seat answers with
 * this, at every level, and the search predicts a muster with it, so what the search expects is
 * what the table plays.
 */
export function chooseMusterResponse(ctx: GameContext, state: GameState): Move {
  const pass: Move = { type: 'pass-battle' };
  const battle = state.pendingBattle;
  const faction = battle?.responders?.[battle.responseIndex ?? 0];
  if (state.phase !== 'muster' || !faction) return pass;
  let best: Move = pass;
  let bestValue = fightValue(ctx, settled(ctx, applyMove(ctx, state, pass)), faction);
  for (const move of legalMoves(ctx, state)) {
    if (move.type !== 'join-battle') continue;
    const value =
      fightValue(ctx, settled(ctx, applyMove(ctx, state, move)), faction) - MUSTER_TURN_COST;
    if (value > bestValue) {
      best = move;
      bestValue = value;
    }
  }
  return best;
}

/** The muster with every later responder passing: the fight as it would then be fought. */
function settled(ctx: GameContext, state: GameState): GameState {
  while (state.phase === 'muster') state = applyMove(ctx, state, { type: 'pass-battle' });
  return state;
}

/**
 * The expected change the pending fight makes to `faction`'s material, every other side counting
 * against it: whoever loses leaves the board, and the winner carries its wounds onto the square.
 */
function fightValue(ctx: GameContext, state: GameState, faction: FactionId): number {
  const battle = state.pendingBattle;
  if (state.phase !== 'battle' || !battle) return 0;
  const sign = (piece: Piece) => (piece.faction === faction ? 1 : -1);
  const summoned = (piece: Piece) => ctx.roster[piece.character]?.summoned === true;
  const powerPoint = ctx.board.cells[battle.y * ctx.board.width + battle.x]?.powerPoint === true;
  const holder = pieceById(state, battle.defenderId);
  const winners = fightWinners(ctx, state);
  const pieces = winners.map((w) => pieceById(state, w.pieceId)!);
  const worth = pieces.map((piece) => pieceWorth(ctx, state, piece));
  let total = 0;
  winners.forEach(({ p, winnerDamage }, i) => {
    if (p <= 0) return;
    const winner = pieces[i]!;
    let change = 0;
    pieces.forEach((piece, j) => {
      if (j !== i || summoned(piece)) change -= sign(piece) * worth[j]!;
    });
    if (!summoned(winner)) {
      const wounded = { ...winner, x: battle.x, y: battle.y, damage: winnerDamage };
      change += sign(winner) * (pieceWorth(ctx, state, wounded) - worth[i]!);
    }
    if (powerPoint) {
      const keeps = summoned(winner) ? 0 : sign(winner);
      change += POWER_POINT_VALUE * (keeps - (holder ? sign(holder) : 0));
    }
    total += p * change;
  });
  return total;
}
